import { useState, useEffect, useRef } from 'react';
import lcaClient, {
  downloadLcaTemplate,
  importErrorSummary,
  uploadErrorMessage,
} from '../../../../services/lcaClient';
const api = lcaClient;
import { toast } from 'sonner';
import { getEmptyRow } from '../constants';

export function useInventoryData(project, onUpdate) {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [activeTab, setActiveTab] = useState('bom');
  const [locking, setLocking] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [newRow, setNewRow] = useState(null);
  const fileInputRef = useRef(null);

  // Gate-to-Grave state
  const [goalScope, setGoalScope] = useState(null);
  const [g2gSettings, setG2gSettings] = useState(null);
  const [g2gSummary, setG2gSummary] = useState(null);
  const [showG2gSection, setShowG2gSection] = useState(false);
  const [savingG2gSettings, setSavingG2gSettings] = useState(false);
  const [productLifetime, setProductLifetime] = useState('');
  const [lifetimeGeography, setLifetimeGeography] = useState('Global');

  // Derived state
  const isCradleToGrave = goalScope?.system_boundary === 'cradle_to_grave';
  const filteredInventory = inventory.filter(item => item.category === activeTab);
  const counts = {
    bom: inventory.filter(i => i.category === 'bom').length,
    energy: inventory.filter(i => i.category === 'energy').length,
    transport: inventory.filter(i => i.category === 'transport').length,
    process: inventory.filter(i => i.category === 'process').length,
    use_phase: inventory.filter(i => i.category === 'use_phase').length,
    maintenance: inventory.filter(i => i.category === 'maintenance').length,
    eol: inventory.filter(i => i.category === 'eol').length
  };

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      // Scope user datasets to the project's site so an inventory item picker
      // never lists overrides that belong to a different site. System
      // datasets remain tenant-wide (always shown).
      const dsParams = project.site_id ? { params: { site_id: project.site_id } } : {};
      const [invResponse, dsResponse, gsResponse] = await Promise.all([
        api.get(`/projects/${project.id}/inventory`),
        api.get('/datasets', dsParams),
        api.get(`/projects/${project.id}/goal-scope`).catch(() => ({ data: null }))
      ]);
      setInventory(invResponse.data);
      setDatasets(dsResponse.data);
      setGoalScope(gsResponse.data);

      // If cradle_to_grave, fetch G2G settings and summary
      if (gsResponse.data?.system_boundary === 'cradle_to_grave') {
        try {
          const [settingsRes, summaryRes] = await Promise.all([
            api.get(`/projects/${project.id}/gate-to-grave-settings`).catch(() => ({ data: null })),
            api.get(`/projects/${project.id}/gate-to-grave-summary`).catch(() => ({ data: null }))
          ]);
          if (settingsRes.data) {
            setG2gSettings(settingsRes.data);
            setProductLifetime(settingsRes.data.product_lifetime_years?.toString() || '');
            setLifetimeGeography(settingsRes.data.lifetime_geography || 'Global');
          }
          if (summaryRes.data) {
            setG2gSummary(summaryRes.data);
          }
        } catch (e) {
          // G2G settings might not exist yet
        }
      }
    } catch (error) {
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const addNewRow = () => {
    setNewRow(getEmptyRow(activeTab));
  };

  const cancelNewRow = () => {
    setNewRow(null);
  };

  const saveNewRow = async () => {
    // Validate required fields
    if (!newRow.name) {
      toast.error('Please select a dataset item');
      return;
    }

    if (!newRow.dataset_id) {
      toast.error('Please select a valid item from the dataset library');
      return;
    }

    if (activeTab === 'transport') {
      if (!newRow.distance_km || !newRow.load_kg) {
        toast.error('Distance and Load are required for transport');
        return;
      }
    } else {
      if (!newRow.quantity) {
        toast.error('Quantity is required');
        return;
      }
    }

    // G2G specific validations
    if (activeTab === 'maintenance' && !newRow.replacement_interval_years) {
      toast.error('Replacement interval is required for maintenance items');
      return;
    }

    try {
      const payload = {
        category: activeTab,
        name: newRow.name,
        quantity: activeTab === 'transport'
          ? (parseFloat(newRow.distance_km) * parseFloat(newRow.load_kg)) / 1000
          : parseFloat(newRow.quantity) || 0,
        unit: activeTab === 'transport' ? 'tkm' : newRow.unit,
        dataset_id: newRow.dataset_id,
        geography: newRow.geography || 'Global',
        data_quality: newRow.data_quality || 'medium',
        data_source: 'generic',
        additional_data: {}
      };

      // Category-specific fields
      if (activeTab === 'transport') {
        payload.additional_data.distance_km = parseFloat(newRow.distance_km) || 0;
        payload.additional_data.load_kg = parseFloat(newRow.load_kg) || 0;
      }
      if (activeTab === 'process') {
        if (newRow.yield_percent) payload.additional_data.yield_percent = parseFloat(newRow.yield_percent);
        if (newRow.scrap_percent) payload.additional_data.scrap_percent = parseFloat(newRow.scrap_percent);
      }

      // Gate-to-Grave specific fields
      if (activeTab === 'use_phase') {
        payload.usage_basis = newRow.usage_basis || 'per_year';
      }
      if (activeTab === 'maintenance') {
        payload.component_name = newRow.component_name || '';
        payload.replacement_interval_years = parseFloat(newRow.replacement_interval_years) || 0;
      }
      if (activeTab === 'eol') {
        payload.eol_method = newRow.eol_method || 'cut_off';
      }

      const response = await api.post(`/projects/${project.id}/inventory`, payload);
      setInventory([...inventory, response.data]);
      setNewRow(null);
      toast.success('Item added');

      // Refresh G2G summary if applicable
      if (['use_phase', 'maintenance', 'eol'].includes(activeTab)) {
        try {
          const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
          setG2gSummary(summaryRes.data);
        } catch (e) {}
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const startEditing = (item) => {
    setEditingRowId(item.id);
    setEditingData({
      name: item.name,
      dataset_id: item.dataset_id,
      emission_factor: item.emission_factor,
      quantity: item.quantity,
      unit: item.unit,
      geography: item.geography || 'Global',
      data_quality: item.data_quality || 'medium',
      distance_km: item.additional_data?.distance_km || '',
      load_kg: item.additional_data?.load_kg || '',
      yield_percent: item.additional_data?.yield_percent || '',
      scrap_percent: item.additional_data?.scrap_percent || ''
    });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const saveEditing = async (item) => {
    if (!editingData.name || !editingData.dataset_id) {
      toast.error('Please select a valid item from the dataset library');
      return;
    }

    try {
      const payload = {
        category: item.category,
        name: editingData.name,
        quantity: item.category === 'transport'
          ? (parseFloat(editingData.distance_km) * parseFloat(editingData.load_kg)) / 1000
          : parseFloat(editingData.quantity) || 0,
        unit: item.category === 'transport' ? 'tkm' : editingData.unit,
        dataset_id: editingData.dataset_id,
        geography: editingData.geography || 'Global',
        data_quality: editingData.data_quality || 'medium',
        data_source: 'generic',
        additional_data: {}
      };

      if (item.category === 'transport') {
        payload.additional_data.distance_km = parseFloat(editingData.distance_km) || 0;
        payload.additional_data.load_kg = parseFloat(editingData.load_kg) || 0;
      }
      if (item.category === 'process') {
        if (editingData.yield_percent) payload.additional_data.yield_percent = parseFloat(editingData.yield_percent);
        if (editingData.scrap_percent) payload.additional_data.scrap_percent = parseFloat(editingData.scrap_percent);
      }

      const response = await api.put(`/projects/${project.id}/inventory/${item.id}`, payload);
      setInventory(inventory.map(i => i.id === item.id ? response.data : i));
      setEditingRowId(null);
      setEditingData({});
      toast.success('Item updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await api.delete(`/projects/${project.id}/inventory/${itemId}`);
      const deletedItem = inventory.find(i => i.id === itemId);
      setInventory(inventory.filter(i => i.id !== itemId));
      toast.success('Item deleted');

      // Refresh G2G summary if applicable
      if (deletedItem && ['use_phase', 'maintenance', 'eol'].includes(deletedItem.category)) {
        try {
          const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
          setG2gSummary(summaryRes.data);
        } catch (e) {}
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const saveG2gSettings = async () => {
    if (!productLifetime || parseFloat(productLifetime) <= 0) {
      toast.error('Product lifetime must be greater than 0');
      return;
    }

    setSavingG2gSettings(true);
    try {
      const response = await api.post(`/projects/${project.id}/gate-to-grave-settings`, {
        product_lifetime_years: parseFloat(productLifetime),
        lifetime_geography: lifetimeGeography || 'Global'
      });
      setG2gSettings(response.data);
      toast.success('Product lifetime saved');

      // Refresh summary
      const summaryRes = await api.get(`/projects/${project.id}/gate-to-grave-summary`);
      setG2gSummary(summaryRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingG2gSettings(false);
    }
  };

  const lockPhase = async () => {
    if (inventory.length === 0) {
      toast.error('Add at least one inventory item before locking');
      return;
    }

    // A linked item has a dataset_id. We do NOT require a scalar emission_factor:
    // BAFU reference datasets characterized for non-climate indicators (e.g. UBP /
    // Ecological Scarcity) legitimately have a null gwp100/emission_factor, yet are
    // valid — the LCIA engine resolves per-method characterization from the
    // reference library. Legacy system/user datasets always carry a non-null factor
    // (required at creation), so dataset_id alone is a sufficient "linked" check.
    const itemsWithoutDataset = inventory.filter(i => !i.dataset_id);
    if (itemsWithoutDataset.length > 0) {
      toast.error(`${itemsWithoutDataset.length} item(s) are missing dataset references. Please ensure all items are linked to datasets.`);
      return;
    }

    // Gate-to-Grave validation
    if (isCradleToGrave) {
      if (!g2gSettings?.product_lifetime_years) {
        toast.error('Product lifetime is required for Gate-to-Grave assessment');
        return;
      }

      const usePhaseItems = inventory.filter(i => i.category === 'use_phase');
      const eolItems = inventory.filter(i => i.category === 'eol');

      if (usePhaseItems.length === 0) {
        toast.error('At least one Use Phase item is required for Gate-to-Grave assessment');
        return;
      }

      if (eolItems.length === 0) {
        toast.error('At least one End-of-Life item is required for Gate-to-Grave assessment');
        return;
      }
    }

    setLocking(true);
    try {
      await api.post(`/projects/${project.id}/inventory/lock`);
      toast.success('Phase 2 locked successfully');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to lock phase');
    } finally {
      setLocking(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Unlocking Phase 2 will reset Phase 3 (LCIA) and Phase 4 (Interpretation) to draft status. LCIA results and AI insights will be deleted. Do you want to continue?')) {
      return;
    }

    setUnlocking(true);
    try {
      await api.post(`/projects/${project.id}/inventory/unlock`);
      toast.success('Phase 2 unlocked. You can now edit inventory data.');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock phase');
    } finally {
      setUnlocking(false);
    }
  };

  const downloadTemplate = async (category) => {
    try {
      await downloadLcaTemplate(
        `/inventory/template/${category}`,
        `${category}_template.csv`
      );
      toast.success('Template downloaded');
    } catch (error) {
      toast.error(error.message || 'Failed to download template');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `/projects/${project.id}/inventory/upload/${activeTab}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const { items_created: created = 0, errors = [] } = response.data || {};
      if (errors.length > 0) {
        // The import is all-or-nothing, so nothing was written. Show the
        // actual row messages rather than just a count.
        // whiteSpace preserves the newlines importErrorSummary puts between
        // row messages; without it they run together on one line.
        toast.error(importErrorSummary(errors), {
          description: 'Nothing was imported — fix the file and try again.',
          style: { whiteSpace: 'pre-line' },
        });
      } else if (created > 0) {
        toast.success(`${created} items imported`);
        fetchData();
      } else {
        toast.warning('No rows found in that file — nothing was imported');
      }
    } catch (error) {
      toast.error(uploadErrorMessage(error));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDatasetSelect = (dataset, setData, data) => {
    setData({
      ...data,
      name: dataset.name,
      dataset_id: dataset.id,
      emission_factor: dataset.emission_factor,
      // Reference picks carry the dataset's unit/geography; quantities must
      // be entered in that unit (backend converts only same-dimension units).
      ...(dataset.unit ? { unit: dataset.unit } : {}),
      ...(dataset.geography ? { geography: dataset.geography } : {}),
      dataset_source_type: dataset.source_type || null
    });
  };

  return {
    // State
    loading,
    inventory,
    datasets,
    activeTab, setActiveTab,
    locking,
    unlocking,
    uploading,
    editingRowId, setEditingRowId,
    editingData, setEditingData,
    newRow, setNewRow,
    fileInputRef,
    goalScope,
    g2gSettings,
    g2gSummary,
    showG2gSection, setShowG2gSection,
    savingG2gSettings,
    productLifetime, setProductLifetime,
    lifetimeGeography, setLifetimeGeography,

    // Derived
    isCradleToGrave,
    filteredInventory,
    counts,

    // Actions
    fetchData,
    addNewRow,
    cancelNewRow,
    saveNewRow,
    startEditing,
    cancelEditing,
    saveEditing,
    deleteItem,
    saveG2gSettings,
    lockPhase,
    handleUnlock,
    downloadTemplate,
    handleFileUpload,
    handleDatasetSelect
  };
}
